// src/components/subscriptions/ManualPaymentModal.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './ManualPaymentModal.css'; // Ensure the CSS file is imported

const ManualPaymentModal = ({
    paymentData,
    gatewayDetails,
    organizationCountryCode,
    onConfirm,
    onClose,
    onRequestDiscount,
    hasDiscount
}) => {
    const navigate = useNavigate();
    const [confirming, setConfirming] = useState(false);
    const [copied, setCopied] = useState({});

    const normalizedCountryCode = (organizationCountryCode || '').toUpperCase();
    const gatewayConfig = gatewayDetails?.configuration || {};
    const countryAccounts = gatewayConfig?.bank_accounts_by_country;

    // Resolve bank account details by org country, with fallback to DEFAULT/* and legacy flat config.
    const bankDetails = (
        (countryAccounts && normalizedCountryCode && countryAccounts[normalizedCountryCode]) ||
        (countryAccounts && (countryAccounts.DEFAULT || countryAccounts['*'] || countryAccounts.default)) ||
        gatewayConfig
    ) || {
        // Fallback static details if configuration is missing (shouldn't happen if gatewayDetails is correctly passed)
        bank_name: 'Default Bank Name',
        account_name: 'Default Account Name',
        account_number: '0000000000',
        swift_code: 'DEFAULT',
        bank_address: 'Default Bank Address',
        additional_info: 'Please contact support for payment details.',
        processing_time: 'Processing time unknown'
    };

    const handleCopy = (field, value) => {
        if (!value) return; // Guard clause if value is null/undefined
        navigator.clipboard.writeText(value);
        setCopied({ ...copied, [field]: true });
        setTimeout(() => {
            setCopied({ ...copied, [field]: false });
        }, 2000);
    };

    const handleConfirmPayment = async () => {
        if (!window.confirm('Have you completed the bank transfer and want to notify us?')) {
            return;
        }

        setConfirming(true);
        try {
            await onConfirm();
            // Close the modal
            onClose();
            // Redirect to subscriptions page
            navigate('/subscriptions');
        } catch (error) {
            alert('Failed to confirm payment. Please try again.');
            setConfirming(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="manual-payment-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>Bank Transfer Details</h3>
                    <button className="close-btn" onClick={onClose}>
                        <i className="fas fa-times"></i>
                    </button>
                </div>

                <div className="modal-body">
                    <div className="payment-info-box">
                        <div className="info-icon">
                            <i className="fas fa-info-circle"></i>
                        </div>
                        <div className="info-content">
                            <h4>Payment Instructions</h4>
                            <p>
                                Please transfer the exact amount to the bank account below.
                                After making the transfer, click "I Have Made Payment" to notify us.
                                Your license will be activated within {bankDetails.processing_time || '24 hours'} of verification.
                            </p>
                        </div>
                    </div>

                    <div className="payment-amount-card">
                        <div className="amount-label">Amount to Pay</div>
                        <div className="amount-value">
                            {paymentData.currency} {parseFloat(paymentData.amount).toFixed(2)}
                        </div>
                        <div className="payment-reference">
                            Reference: <strong>{paymentData.reference}</strong>
                            <button
                                className="copy-btn copy-btn-light"
                                onClick={() => handleCopy('reference', paymentData.reference)}
                            >
                                <i className={copied.reference ? 'fas fa-check' : 'fas fa-copy'}></i>
                            </button>
                        </div>
                    </div>

                    <div className="bank-details-section">
                        <h4>Bank Account Details</h4>
                        {normalizedCountryCode && (
                            <div className="detail-row">
                                <div className="detail-label">
                                    <i className="fas fa-globe"></i>
                                    Country
                                </div>
                                <div className="detail-value">
                                    {normalizedCountryCode}
                                </div>
                            </div>
                        )}

                        <div className="detail-row">
                            <div className="detail-label">
                                <i className="fas fa-university"></i>
                                Bank Name
                            </div>
                            <div className="detail-value">
                                {bankDetails.bank_name}
                                <button
                                    className="copy-btn"
                                    onClick={() => handleCopy('bank_name', bankDetails.bank_name)}
                                >
                                    <i className={copied.bank_name ? 'fas fa-check' : 'fas fa-copy'}></i>
                                </button>
                            </div>
                        </div>

                        <div className="detail-row">
                            <div className="detail-label">
                                <i className="fas fa-user"></i>
                                Account Name
                            </div>
                            <div className="detail-value">
                                {bankDetails.account_name}
                                <button
                                    className="copy-btn"
                                    onClick={() => handleCopy('account_name', bankDetails.account_name)}
                                >
                                    <i className={copied.account_name ? 'fas fa-check' : 'fas fa-copy'}></i>
                                </button>
                            </div>
                        </div>

                        <div className="detail-row highlight">
                            <div className="detail-label">
                                <i className="fas fa-credit-card"></i>
                                Account Number
                            </div>
                            <div className="detail-value">
                                <strong>{bankDetails.account_number}</strong>
                                <button
                                    className="copy-btn"
                                    onClick={() => handleCopy('account_number', bankDetails.account_number)}
                                >
                                    <i className={copied.account_number ? 'fas fa-check' : 'fas fa-copy'}></i>
                                </button>
                            </div>
                        </div>

                        {bankDetails.swift_code && (
                            <div className="detail-row">
                                <div className="detail-label">
                                    <i className="fas fa-code"></i>
                                    SWIFT Code
                                </div>
                                <div className="detail-value">
                                    {bankDetails.swift_code}
                                    <button
                                        className="copy-btn"
                                        onClick={() => handleCopy('swift_code', bankDetails.swift_code)}
                                    >
                                        <i className={copied.swift_code ? 'fas fa-check' : 'fas fa-copy'}></i>
                                    </button>
                                </div>
                            </div>
                        )}

                        {bankDetails.bank_address && (
                            <div className="detail-row">
                                <div className="detail-label">
                                    <i className="fas fa-map-marker-alt"></i>
                                    Bank Address
                                </div>
                                <div className="detail-value">
                                    {bankDetails.bank_address}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="important-notes">
                        <h4>
                            <i className="fas fa-exclamation-circle"></i>
                            Important Notes
                        </h4>
                        <ul>
                            <li>
                                <i className="fas fa-check"></i>
                                Transfer the <strong>exact amount</strong> shown above
                            </li>
                            <li>
                                <i className="fas fa-check"></i>
                                Include the payment reference <strong>{paymentData.reference}</strong> in your transfer description
                            </li>
                            <li>
                                <i className="fas fa-check"></i>
                                Keep your payment receipt for verification
                            </li>
                            <li>
                                <i className="fas fa-check"></i>
                                Click "I Have Made Payment" after completing the transfer
                            </li>
                            <li>
                                <i className="fas fa-check"></i>
                                Your license will be activated within <strong>{bankDetails.processing_time || '24 hours'}</strong> of payment verification
                            </li>
                        </ul>
                    </div>

                    {bankDetails.additional_info && (
                        <div className="additional-info">
                            <i className="fas fa-info-circle"></i>
                            <p>{bankDetails.additional_info}</p>
                        </div>
                    )}

                    <div className="email-notice">
                        <i className="fas fa-envelope"></i>
                        <p>
                            These payment details have been sent to your email address.
                            You can return to this page anytime from your subscription dashboard.
                        </p>
                    </div>
                </div>

                <div className="modal-footer">
                    <div className="footer-left">
                        <button
                            className="later-btn"
                            onClick={onClose}
                        >
                            I'll Pay Later
                        </button>
                        {!hasDiscount && onRequestDiscount && (
                            <button
                                className="discount-btn"
                                onClick={() => {
                                    onClose();
                                    onRequestDiscount();
                                }}
                                title="Request a discount for your subscription"
                            >
                                <i className="fas fa-tag"></i>
                                Request Discount
                            </button>
                        )}
                    </div>
                    <button
                        className="confirm-payment-btn"
                        onClick={handleConfirmPayment}
                        disabled={confirming}
                    >
                        {confirming ? (
                            <>
                                <span className="spinner-small"></span>
                                Processing...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-check-circle"></i>
                                I Have Made Payment
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ManualPaymentModal;
